import React from 'react';
import { Typography } from '@mui/material';
import Navbar from '../Header/Navbar';
import Footer from '../Header/Footer';
import { Stack } from '@mui/system';
import { Circle } from '@mui/icons-material';
import { SectionList } from '../Sections/SectionElements';

const TermsAndConditions = () => {
  return (
    <Stack gap={3}>
      <Navbar />

      <Stack sx={{
        width: '100%', alignItems: 'center', boxSizing: 'border-box', p: '20px',
        '& .section': {
          width: '100%', borderRadius: '16px', p: '16px', gap: 2,
          bgcolor: 'var(--color-bg-secondary)',
          boxSizing: 'border-box',
        },

        '& .heading': {
          fontSize: '32px', fontWeight: 'bold', paddingBottom: '4px',
          borderBottom: '1px solid var(--color-border)',
        },

        '& .content': {
          fontSize: '16px', color: 'var(--color-text-secondary)'
        }
      }} >
        <Stack sx={{ width: '100%', maxWidth: '1080px', gap: 3, alignItems: 'flex-start' }} >
          <Stack className='section' style={{ backgroundColor: 'transparent' }} >
            <Typography className='heading' >
              Terms and Conditions (SaaS platform)
            </Typography>

            <Typography className='content' >
              These Terms and Conditions ("Terms") govern your access to and use of the Optigrit platform ("Platform") for learning content management, including any services, functionalities, content, or applications offered by Optigrit (collectively, the "Services"). By accessing or using the Services, you agree to be bound by these Terms.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              01 Acceptance
            </Typography>
            <Typography className="content" >
              By accessing or using the Services, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access or use the Services.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              02 Description of Services
            </Typography>

            <Typography className="content" >
              Optigrit provides a software-as-a-service (SaaS) solution that empowers organizations and individuals to create and manage their own learning experiences. In just minutes, you can set up a powerful platform to:
            </Typography>

            <SectionList
              icon={<Circle sx={{ fontSize: '10px' }} />}
              list={[
                {
                  label: 'Effortless Setup',
                  content: 'Create your organization with a few clicks and a user-friendly interface.'
                },
                {
                  label: 'Complete Customization',
                  content: 'Upload logos, banners, and customize the look and feel to match your brand identity.'
                },
                {
                  label: 'Content Management',
                  content: 'Upload any type of learning material, including videos, presentations, documents, and more.'
                },
                {
                  label: 'Course Creation (Optional)',
                  content: 'Structure your content into organized learning paths with modules, quizzes, and assessments.'
                },
                {
                  label: 'Task Management',
                  content: 'Assign tasks to individuals or teams, set deadlines, and track completion. Tasks can be linked to specific courses or be completely custom.'
                },
                {
                  label: 'Contest Management (Under Development)',
                  content: 'Run frontend development contests using APIs. The platform can analyze community reviews to identify valuable feedback and score submissions.'
                },
                {
                  label: 'Progress Tracking',
                  content: 'Gain insights into learner engagement and completion rates.'
                },
              ]}

              renderItem={({label, content}) => (
                <Typography className='content' >
                  <strong>{label}:</strong> {content}
                </Typography>
              )}
            />
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              03 User Accounts
            </Typography>

            <Typography className="content" >
              You may need to create an account to access certain features of the Services. You are responsible for maintaining the confidentiality of your account credentials and are fully responsible for all activities that occur under your account. You agree to notify Optigrit immediately of any unauthorized use of your account or any other breach of security. Optigrit reserves the right to suspend or terminate your account at any time without notice for any reason, including, but not limited to, breach of these Terms.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              04 Content Ownership
            </Typography>

            <Typography className="content" >
              You retain all ownership rights to the content you upload to the Platform ("Your Content"). You grant Optigrit a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, publish, and distribute Your Content solely for the purpose of providing the Services.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              05 User Conduct
            </Typography>

            <Typography className="content" >
              You agree to use the Services in a lawful and respectful manner. You will not:
            </Typography>
            
            <SectionList
              icon={<Circle sx={{ fontSize: '10px' }} />}
              list={[
                'Upload any content that is illegal, harmful, threatening, abusive, harassing, defamatory, obscene, hateful, or racially or ethnically offensive.',
                'Violate any intellectual property rights of others.',
                'Transmit any viruses, worms, Trojan horses, or other malicious code.',
                'Interfere with or disrupt the Services or servers.',
                'Use the Services for any unauthorized commercial purpose.',
              ]}
              renderItem={item => (
                <Typography className='content' >
                  {item}
                </Typography>
              )}
            />
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              06 Disclaimers
            </Typography>

            <Typography className="content" >
              The Services are provided "as is" and "as available" without warranty of any kind, express or implied. Optigrit disclaims all warranties, including, but not limited to, warranties of merchantability, fitness for a particular purpose, and non-infringement. Optigrit does not warrant that the Services will be uninterrupted, secure, or error-free. You use the Services at your own risk.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              07 Limitation of Liability
            </Typography>

            <Typography className="content" >
              Optigrit will not be liable for any damages arising out of or related to your use of the Services, including, but not limited to, direct, indirect, incidental, consequential, or punitive damages.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              08 Term and Termination
            </Typography>

            <Typography className="content" >
              These Terms will remain in effect until terminated by you or Optigrit. You may terminate these Terms at any time by discontinuing your use of the Services. Optigrit may terminate these Terms for any reason at any time by providing you with notice.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              09 Governing Law
            </Typography>

            <Typography className="content" >
              These Terms will be governed by and construed in accordance with the laws of India.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              10 Entire Agreement
            </Typography>

            <Typography className="content" >
              These Terms constitute the entire agreement between you and Optigrit regarding your use of the Services.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              11 Severability
            </Typography>

            <Typography className="content" >
              If any provision of these Terms is held to be invalid or unenforceable, such provision shall be struck and the remaining provisions shall remain in full force and effect.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              12 Waiver
            </Typography>

            <Typography className="content" >
              No waiver of any provision of these Terms shall be deemed a further or continuing waiver of such provision or any other provision.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              13 Updates to Terms
            </Typography>

            <Typography className="content" >
              Optigrit may update these Terms at any time. The updated Terms will be effective upon posting on the Platform. You are advised to review these Terms periodically for any changes.
            </Typography>
          </Stack>

          <Stack className="section" >
            <Typography className="heading" >
              14 Contact Us
            </Typography>

            <Typography className="content" >
              If you have any questions about these Terms, please contact us at support@optigrit.com.
            </Typography>
          </Stack>
        </Stack>
      </Stack>

      <Footer />
    </Stack>
  );
};

export default TermsAndConditions;
